import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, HelpCircle } from "lucide-react";
import { useEffect } from "react";
import {
  useQuotationInvoiceDetailQuery,
  useUpdateQuotationInvoiceDocument,
} from "../api/queries";
import {
  DocumentEditTour,
  useDocumentEditTour,
} from "../tour/DocumentEditTour";
import { DocumentCreateForm } from "../ui/DocumentCreateForm";
import { DocumentTypeManagementModal } from "../ui/DocumentTypeManagementModal";

export function QuotationInvoiceDocumentEditPage() {
  const { id } = useParams({ from: "/app/documents/$id/edit" });
  const nav = useNavigate({ from: "/app/documents/$id/edit" });
  const { data, isLoading } = useQuotationInvoiceDetailQuery(id ?? "");
  const updateMutation = useUpdateQuotationInvoiceDocument();
  const tour = useDocumentEditTour();
  const documentTypeModal = useDisclosure();
  const canUpdate = useActionPermission(["quotation_invoice:update"]);

  useEffect(() => {
    if (!isLoading && data?.document.status !== "draft" && id) {
      nav({ to: "/app/documents/$id", params: { id } });
    }
  }, [data?.document.status, id, isLoading, nav]);

  const isRedirecting = !isLoading && data && data.document.status !== "draft";
  if (isRedirecting) return null;

  const handleBack = () =>
    nav({ to: "/app/documents/$id", params: { id: id ?? "" } });

  const handleSubmit = (payload: {
    documentType: string;
    status: "draft" | "issued";
    documentNumber: string;
    customerId: string | null;
    customerName: string;
    customerAddress: string | null;
    customerContact: string | null;
    issueDate: string | null;
    dueDate: string | null;
    referencePo: string | null;
    paymentTerms: string | null;
    notes: string | null;
    templateId: string | null;
    items: Array<{
      description: string;
      detail?: string | null;
      quantity: number;
      unit: string | null;
      unitPrice: number;
      discountAmount?: number;
      taxRate?: number;
    }>;
    discountAmount: number;
    taxRate: number;
    depositPercent: number;
    currency: string;
    showSignature: boolean;
  }) => {
    if (!id) return;
    updateMutation.mutate(
      {
        id,
        input: {
          documentType: payload.documentType,
          status: payload.status,
          documentNumber: payload.documentNumber,
          customerId: payload.customerId ?? null,
          customerName: payload.customerName,
          customerAddress: payload.customerAddress ?? null,
          customerContact: payload.customerContact ?? null,
          issueDate: payload.issueDate ?? null,
          dueDate: payload.dueDate ?? null,
          currency: payload.currency,
          paymentTerms: payload.paymentTerms ?? null,
          notes: payload.notes ?? null,
          templateId: payload.templateId ?? null,
          items: payload.items.map((row) => ({
            description: row.description,
            detail: row.detail ?? null,
            quantity: row.quantity,
            unit: row.unit ?? null,
            unitPrice: row.unitPrice,
          })),
          discountAmount: payload.discountAmount,
          taxRate: payload.taxRate,
          depositPercent: payload.depositPercent,
          showSignature: payload.showSignature,
        },
      },
      {
        onSuccess: () => {
          nav({ to: "/app/documents/$id", params: { id } });
        },
      },
    );
  };

  return (
    <>
      <Header />
      <Main>
        <DocumentEditTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div className="space-y-2">
          <div className="mb-4 flex items-center justify-between space-x-2">
            <div
              className="flex items-center gap-2"
              data-tourid="document-edit-back"
            >
              <h1 className="font-bold text-2xl tracking-tight">ແກ້ໄຂເອກະສານ</h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={handleBack}
              className="gap-2"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              ກັບໄປລາຍການເອກະສານ
            </Button>
          </div>
        </div>
        <DocumentTypeManagementModal
          open={documentTypeModal.isOpen}
          onOpenChange={(open) =>
            open ? documentTypeModal.open() : documentTypeModal.close()
          }
        />

        {isLoading && <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>}
        {data && data.document.status === "draft" && (
          <div data-tourid="document-edit-form">
            <DocumentCreateForm
              key={data.document.id}
              initialData={{
                document: data.document,
                items: data.items,
              }}
              onSubmit={handleSubmit}
              submitting={updateMutation.isPending}
              onOpenDocumentTypeModal={
                canUpdate ? documentTypeModal.open : undefined
              }
            />
          </div>
        )}
      </Main>
    </>
  );
}
